import React, { memo, useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import Table from '@components/common/Table';

import useQueryDashboardRequestStatusesSummaryByStatus
	from '@hooks/dashboardPO/useQueryDashboardRequestStatusesSummaryByStatus';

import {
	ISortBy,
	ITablePagination,
	ITableSearch, ITableSort,
} from '@interfaces/components/table';
import { IDashboardRequestStatus } from '@interfaces/platform/platformDashboard';

import { defaultMeta } from '@enums/common';

import { getColumns } from './utils';

import styles from './DashboardPOByStatusTable.module.scss';

interface IDashboardPOByStatusTable {
	status: IDashboardRequestStatus;
}

const TABLE_HEIGHT = '555px';

const DashboardPOByStatusTable = ({ status }: IDashboardPOByStatusTable) => {
	const navigate = useNavigate();

	const { name: statusName, title: statusTitle } = status;

	const [page, setPage] = useState(defaultMeta.page);
	const [pageSize, setPageSize] = useState(defaultMeta.pageSize);
	const [sortDirection, setSortDirection] = useState(false);
	const [sortFieldName, setSortFieldName] = useState<string | null>(null);
	const [searchValue, setSearchValue] = useState('');

	const {
		dashboardRequestStatusesSummaryByStatus,
		isLoadingDashboardRequestStatusesSummaryByStatus,
		countOfRecords,
	} = useQueryDashboardRequestStatusesSummaryByStatus({
		page,
		pageSize,
		status: statusName,
		searchValue,
		sortDirection,
		sortFieldName,
	});

	const columns = useMemo(() => getColumns({ navigate, statusTitle }), [navigate, statusTitle]);

	const searchHandler = useCallback((searchValue: string) => {
		setSearchValue(searchValue);
		setPage(defaultMeta.page);
	}, []);

	const search: ITableSearch[] = useMemo(() => [{
		initialValue: searchValue,
		name: 'name',
		handler: searchHandler,
		tooltipText: 'Search by Organisation name',
	}], [searchValue, searchHandler]);

	const pageHandler = useCallback((page: number) => setPage(page), []);
	const pageSizeHandler = useCallback((pageSize: number) => {
		setPage(defaultMeta.page);
		setPageSize(pageSize);
	}, []);

	const pagination: ITablePagination = useMemo(() => ({
		countOfRecords,
		forcePage: page,
		size: pageSize,
		pageHandler,
		pageSizeHandler,
	}), [countOfRecords, page, pageSize, pageHandler, pageSizeHandler]);

	const handleSort = useCallback((sortBy: Array<ISortBy>) => {
		setSortDirection(!!sortBy[0]?.desc);
		setSortFieldName(sortBy[0]?.id);
	}, []);

	const sort: ITableSort = useMemo(() => ({
		initialState: [{ id: sortFieldName, desc: sortDirection }],
		handleSort,
	}), [sortFieldName, sortDirection, handleSort]);

	return (
		<div className={styles.wrapper}>
			<div className={styles.title}>
				<span>Request status:</span>
				<span>{statusTitle}</span>
			</div>
			<Table
				columns={columns}
				data={dashboardRequestStatusesSummaryByStatus}
				isLoading={isLoadingDashboardRequestStatusesSummaryByStatus}
				isWithoutActions
				isWithoutCheckbox
				isWithoutDeleteAction
				isWithoutFilter
				maxHeight={TABLE_HEIGHT}
				pagination={pagination}
				search={search}
				searchThreshold={0}
				sort={sort}
			/>
		</div>
	);
};

export default memo(DashboardPOByStatusTable);
